import React from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../sections/SectionCard';
import { PanelTable } from '../display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import type { PanelTableColumn } from '../display/PanelTable/types';
import { CUSTOMER_QUOTE_DATA } from '@shared/data';

type EntityType = 'customer' | 'invoice';

export interface QuoteData {
  id: string;
  amount: string;
  status: string;
  quoteNumber: string;
  customer: string;
  expiration: string;
  created: string;
}

interface QuotesSectionProps {
  entityType: EntityType;
  quotesData?: QuoteData[];
  isDetailView?: boolean;
  title?: string;
  className?: string;
}

function getQuotesData(entityType: EntityType, customData?: QuoteData[]): QuoteData[] {
  if (customData) return customData;

  switch (entityType) {
    case 'customer':
      return CUSTOMER_QUOTE_DATA;
    case 'invoice':
      return [];
    default:
      return [];
  }
}

const renderAmount = (value: unknown) => {
  const amount = typeof value === 'string' ? value : '$0.00';
  return (
    <span className="font-medium">
      {amount}
    </span>
  );
};

const renderStatus = (_: unknown, item: QuoteData) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge 
      entityType="quote" 
      status={item.status}
      size="sm"
    />
  </div>
);

export const QuotesSection = React.memo<QuotesSectionProps>(({
  entityType,
  quotesData,
  isDetailView = false,
  title = "Quotes",
  className
}) => {
  const quotes = getQuotesData(entityType, quotesData);

  const columns: PanelTableColumn<QuoteData>[] = [
    {
      key: 'amount',
      label: 'AMOUNT',
      headerClassName: 'w-25',
      render: renderAmount
    },
    {
      key: 'status',
      label: 'STATUS',
      headerClassName: 'w-[90px]',
      render: renderStatus
    },
    {
      key: 'quoteNumber',
      label: 'QUOTE #',
      headerClassName: 'w-[120px]',
      cellClassName: 'font-medium'
    },
    {
      key: 'customer',
      label: 'CUSTOMER',
      headerClassName: 'w-[140px]'
    },
    {
      key: 'expiration',
      label: 'EXPIRATION',
      headerClassName: 'w-[110px]',
      cellClassName: 'whitespace-nowrap'
    },
    {
      key: 'created',
      label: 'CREATED',
      headerClassName: 'w-[110px]',
      cellClassName: 'whitespace-nowrap'
    }
  ];

  const rowActions = (item: QuoteData) => [
    {
      key: 'view-quote',
      label: 'View quote',
      onClick: () => { toast.info(`Viewing quote ${item.quoteNumber}`) }
    },
    {
      key: 'duplicate-quote',
      label: 'Duplicate quote',
      onClick: () => { toast.success(`Quote ${item.quoteNumber} duplicated`) }
    },
    {
      key: 'cancel-quote',
      label: 'Cancel quote',
      onClick: () => { toast.success(`Quote ${item.quoteNumber} cancelled`) }
    },
    {
      key: 'edit-quote',
      label: 'Edit quote',
      onClick: () => { toast.info(`Edit quote ${item.quoteNumber}`) }
    },
    {
      key: 'view-subscription',
      label: 'View subscription',
      onClick: () => { toast.info('Navigating to subscription') }
    },
    {
      key: 'view-invoice',
      label: 'View invoice',
      onClick: () => { toast.info('Navigating to invoice') }
    }
  ];

  return (
    <SectionCard title={title} layout="down" className={className}>
      <PanelTable
        data={quotes}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No quotes found"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

QuotesSection.displayName = 'QuotesSection';